import React, { useEffect, useState } from 'react';

import { TrendingUp, Users, FileText, AlertCircle, ReceiptText } from 'lucide-react';

import AdminLayout from '../../components/layout/AdminLayout';
import { CrosshairFrame } from '../../components/common/Crosshair';
import { InvoiceClient, DashboardStatsDTO, MonthlyTotalDTO } from '../../services/apiClient';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';

type Period = 3 | 6 | 12;

const PERIOD_LABELS: Record<Period, string> = { 3: '3M', 6: '6M', 12: '1Y' };

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `R${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `R${(value / 1_000).toFixed(1)}k`;
  return `R${value.toFixed(2)}`;
}

function formatTimeAgo(dateStr?: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function MetricCard({ label, value, sub, icon: Icon, loading }: {
  label: string; value: string; sub: string; icon: React.ElementType; loading: boolean;
}) {
  return (
    <CrosshairFrame className="cg-card flex-1 p-[22px]">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-['Archivo'] text-[13px] font-semibold text-muted mb-2">{label}</p>
          {loading
            ? <div className="h-9 w-24 bg-[rgba(255,255,255,0.06)] animate-pulse rounded-sm" />
            : <p className="font-['Archivo'] text-[34px] font-black text-accent leading-none tracking-tight">{value}</p>
          }
          <p className="cg-label mt-2">{sub}</p>
        </div>
        <Icon className="w-5 h-5 text-accent opacity-80 mt-1" />
      </div>
    </CrosshairFrame>
  );
}

function BarChart({ data, loading }: { data: MonthlyTotalDTO[]; loading: boolean }) {
  const max = Math.max(...data.map(d => d.total ?? 0), 1);
  const now = new Date();

  if (loading) {
    return (
      <div className="relative h-[140px] flex items-end gap-1 pl-1">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full bg-[rgba(255,255,255,0.05)] animate-pulse" style={{ height: `${30 + Math.random() * 80}px` }} />
            <span className="cg-label">{MONTH_NAMES[i]}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative h-[140px] border-l border-b border-[rgba(255,255,255,0.07)] flex items-end gap-1 pl-1">
      {[25, 50, 75, 100].map(pct => (
        <div key={pct} className="absolute left-0 right-0 border-t border-[rgba(255,255,255,0.04)]" style={{ bottom: `${pct}%` }} />
      ))}
      {data.map((d, i) => {
        const isCurrent = d.year === now.getFullYear() && d.month === now.getMonth() + 1;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full rounded-t-sm"
              style={{
                height: `${((d.total ?? 0) / max) * 130}px`,
                background: isCurrent ? '#92de71' : 'rgba(146,222,113,.45)'
              }}
            />
            <span className="cg-label">{MONTH_NAMES[(d.month ?? 1) - 1]}</span>
          </div>
        );
      })}
    </div>
  );
}

const invoiceClient = new InvoiceClient(API_BASE_URL, axiosInstance);

const Dashboard: React.FC = () => {
  const [period, setPeriod] = useState<Period>(12);
  const [stats, setStats] = useState<DashboardStatsDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    invoiceClient.getDashboardStats(period)
      .then(res => { if (res.success && res.data) setStats(res.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [period]);

  const periodLabel = PERIOD_LABELS[period];

  return (
    <AdminLayout
      activeMenuItem="/admin/dashboard"
      breadcrumbs={[{ label: 'Admin', href: '#' }, { label: 'Dashboard', href: '#' }]}
      allowedRoles={['Admin']}
      requiredPermission="Main.Dashboard.View"
    >
      <div className="flex flex-col gap-[18px]">

        {/* Page-level header with period filter */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-['Archivo'] text-[18px] font-extrabold text-text leading-none">Overview</h2>
            <p className="cg-label mt-1">All metrics below reflect the selected period</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-['Space_Mono'] text-[9px] text-muted2 uppercase tracking-widest mr-1">Period</span>
            <div className="flex gap-1">
              {([3, 6, 12] as Period[]).map(p => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`font-['Space_Mono'] text-[9px] uppercase px-3 py-1.5 cursor-pointer transition-colors ${
                    p === period
                      ? 'bg-accent text-[#06120C] border border-accent'
                      : 'bg-transparent text-muted2 border border-line hover:border-[rgba(255,255,255,0.25)]'
                  }`}
                >
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Metric cards */}
        <div className="flex gap-4">
          <MetricCard
            label="Total Invoiced"
            value={stats ? formatCurrency(stats.totalInvoiced ?? 0) : '—'}
            sub={`Last ${periodLabel}`}
            icon={TrendingUp}
            loading={loading}
          />
          <MetricCard
            label="Active Clients"
            value={stats ? String(stats.activeClients) : '—'}
            sub={`Last ${periodLabel}`}
            icon={Users}
            loading={loading}
          />
          <MetricCard
            label="Paid Invoices"
            value={stats ? String(stats.paidInvoicesCount) : '—'}
            sub={`Last ${periodLabel}`}
            icon={FileText}
            loading={loading}
          />
          <MetricCard
            label="Outstanding"
            value={stats ? String(stats.outstandingCount) : '—'}
            sub={`Last ${periodLabel}`}
            icon={AlertCircle}
            loading={loading}
          />
        </div>

        {/* Chart card */}
        <CrosshairFrame className="cg-card p-[22px]">
          <div className="flex justify-between items-center mb-5">
            <div className="flex items-center gap-3">
              <span className="font-['Archivo'] text-[15px] font-bold text-text">Invoice Overview</span>
              <span className="cg-tag">// monthly totals</span>
            </div>
          </div>
          <BarChart data={stats?.monthlyTotals ?? []} loading={loading} />
        </CrosshairFrame>

        {/* Recent activity */}
        <CrosshairFrame corners={['tl', 'tr']} className="cg-card p-[22px]">
          <p className="font-['Archivo'] text-[15px] font-bold text-text mb-4">Recent Activity</p>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex justify-between items-center py-[11px] border-b border-[rgba(255,255,255,.07)]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[rgba(255,255,255,0.05)] animate-pulse" />
                    <div className="flex flex-col gap-1.5">
                      <div className="h-3 w-28 bg-[rgba(255,255,255,0.06)] animate-pulse rounded-sm" />
                      <div className="h-2.5 w-20 bg-[rgba(255,255,255,0.04)] animate-pulse rounded-sm" />
                    </div>
                  </div>
                  <div className="h-5 w-16 bg-[rgba(255,255,255,0.05)] animate-pulse rounded-sm" />
                </div>
              ))
            : (stats?.recentInvoices ?? []).length === 0
              ? <p className="cg-label py-4 text-center">No invoices in this period.</p>
              : (stats?.recentInvoices ?? []).map((row, i) => (
                <div key={i} className="flex justify-between items-center py-[11px] border-b border-[rgba(255,255,255,.07)]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[rgba(146,222,113,.15)] flex items-center justify-center flex-shrink-0">
                      <ReceiptText className="w-3.5 h-3.5 text-accent" />
                    </div>
                    <div>
                      <p className="font-['Space_Mono'] text-[12px] font-bold text-accent">{row.invoiceNumber}</p>
                      <p className="font-['Archivo'] text-[12px] text-text">{row.clientName}</p>
                      <p className="cg-label">{formatTimeAgo(row.createdUtc?.toString())}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {row.status && (
                      <span className={`cg-badge mb-1 block ${row.status.toLowerCase() === 'paid' ? 'cg-badge-success' : 'cg-badge-info'}`}>
                        {row.status}
                      </span>
                    )}
                    <p className="font-['Space_Mono'] text-[12px] font-bold text-accent">{formatCurrency(row.amount ?? 0)}</p>
                  </div>
                </div>
              ))
          }
        </CrosshairFrame>

      </div>
    </AdminLayout>
  );
};

export default Dashboard;
